const compactNumber = (number: number, language: string): string => {
  return formatArToEn(
    Intl.NumberFormat(language, {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(number),
  );
};
const sarFormat = (number: number, language?: string): string => {
  return formatArToEn(
    Intl.NumberFormat(language ?? 'en', {
      style: 'currency',
      currency: 'SAR',
      maximumFractionDigits: 2,
    }).format(number),
  );
};
/** The Saudi Riyal symbol (﷼). Rendered AFTER the amount per the MS-456
 * invoicing spec — distinct from sarFormat, which prints "SAR 1,234.5" (ISO code,
 * before the number). */
const RIYAL_SYMBOL = '﷼';

/**
 * Format a backend money amount as `1,234.56 ﷼` — grouped thousands, always two
 * decimals, and the ﷼ symbol AFTER the number. Negative-safe (e.g. `-492.00 ﷼`
 * for the commission line) and RTL-safe (amount + symbol kept together with a
 * non-breaking space).
 *
 * Display formatting ONLY: it never adds, subtracts, or multiplies money — it just
 * fixes the decimals of an already-rounded backend figure. Accepts the backend's
 * number or numeric string verbatim; a nullish/NaN input renders as 0.00.
 */
const formatRiyal = (amount: number | string | null | undefined): string => {
  const n = typeof amount === 'string' ? Number(amount) : amount ?? 0;
  const safe = Number.isFinite(n as number) ? (n as number) : 0;
  const formatted = formatArToEn(
    Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(safe),
  );
  return `${formatted} ${RIYAL_SYMBOL}`;
};
const formatArToEn = (s: string) => {
  return String(s).replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)));
};
const formatEnToAr = (s: string) => {
  return String(s).replace(/[0-9]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.charAt(Number(d))));
};
const isNumber = (value?: number) => {
  return typeof value === 'number';
};

export {
  compactNumber,
  sarFormat,
  formatRiyal,
  RIYAL_SYMBOL,
  formatArToEn,
  formatEnToAr,
  isNumber,
};
